/**
 * The DOM layer — stamps a pure {@link ResponsiveImageView} onto an `<img>` and
 * offers the ergonomic one-call entry consumers use after an injection.
 *
 * Mirrors the PHP `ImageOutputFilter::writeBack()` (the DOM write) + its
 * off-limits guard (skip author `srcset`, `data:`, svg). `transform` composes
 * the two lower layers (`buildResponsiveImage` + `applyResponsiveImage`) in a
 * `querySelectorAll` loop with the skip-guard — it does NOT re-implement them
 * (DRY, doc 21 §3.2).
 *
 * Side-effect-free at import: origin resolution happens inside `transform`, not
 * at module top level (doc 21 §9 AC4).
 *
 * @see src/Services/ResponsiveImage/ResponsiveImageBuilder.php (builder consumed here)
 * @see docs/speed-optimization/research/responsive-images/21-js-responsive-image-library-design.md §3.1, §5.2, §9 step 5
 */
import { buildResponsiveImage } from './builder';
import { cloudflareSourceOf } from './strategy';
import type {
  ResponsiveImageSpec,
  ResponsiveImageView,
  TransformOptions,
} from './types';

/** Source URLs the library must never transform (non-raster, inline, or already-CF). */
const SKIP_SRC_SUFFIXES = ['.svg', '.gif'];

/**
 * Whether an `<img>` must be skipped (explicit-opt-in calibration, doc 21 §5.2):
 *  - empty/`""` src (JS placeholders, e.g. `datte-modal-image`);
 *  - `data:` URIs, `.svg`, `.gif` (gain nothing from CF resize);
 *  - an already-`/cdn-cgi/image/`-wrapped src (never double-wrap; protects the
 *    untouched server-rendered initial image);
 *  - a non-empty author `srcset` (a deliberate author signal we must not clobber).
 */
export function isSkippable(img: HTMLImageElement): boolean {
  const src = (img.getAttribute('src') ?? '').trim();
  if (src === '') {
    return true;
  }

  const lower = src.toLowerCase();
  if (lower.startsWith('data:') || SKIP_SRC_SUFFIXES.some((ext) => lower.endsWith(ext))) {
    return true;
  }
  if (src.includes('/cdn-cgi/image/')) {
    return true;
  }

  return (img.getAttribute('srcset') ?? '').trim() !== '';
}

/**
 * Stamp a prebuilt view onto a single `<img>` (mirrors `writeBack()`). `src` is
 * always set; `srcset`/`sizes`/`width`/`height` are set only when non-null (so a
 * fixed lone-step / conservative fallback leaves them absent).
 */
export function applyResponsiveImage(img: HTMLImageElement, view: ResponsiveImageView): void {
  img.setAttribute('src', view.src);

  if (view.srcset !== null) {
    img.setAttribute('srcset', view.srcset);
  }
  if (view.sizes !== null) {
    img.setAttribute('sizes', view.sizes);
  }
  if (view.width !== null) {
    img.setAttribute('width', String(view.width));
  }
  if (view.height !== null) {
    img.setAttribute('height', String(view.height));
  }
}

/**
 * The ergonomic entry: size every `<img>` matching `selector` inside `root`
 * using an explicit {@link ResponsiveImageSpec}. Idempotent + safe via
 * {@link isSkippable}. Returns the count transformed (for tests / diagnostics).
 *
 * The spec is the caller's — typically read from a server-provided
 * `data-rimg-spec` attribute (the PHP SSoT) or a documented inline fallback.
 * There is no named-region registry anymore: the region's `mode`/`maxWidth`/
 * `sizes` reach the JS from the server at runtime, so a consumer passes the spec
 * directly (no JS-side per-region mirror to drift).
 *
 * @param root      the just-injected container
 * @param selector  which imgs inside it to size
 * @param spec      the responsive spec (mode/maxWidth/sizes) to apply
 * @param opts      origin / strategy / source dims (origin defaults to the page origin)
 */
export function transform(
  root: ParentNode,
  selector: string,
  spec: ResponsiveImageSpec,
  opts: TransformOptions = {}
): number {
  const origin =
    opts.origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  const baseSpec = spec;

  let count = 0;
  root.querySelectorAll<HTMLImageElement>(selector).forEach((img) => {
    if (isSkippable(img)) {
      return;
    }
    const rawSrc = img.getAttribute('src') ?? '';
    const view = buildResponsiveImage(
      { ...baseSpec, src: rawSrc },
      { origin, strategy: opts.strategy, source: opts.source }
    );
    applyResponsiveImage(img, view);
    count++;
  });

  return count;
}

/**
 * Undo a Cloudflare resize on one `<img>`: point `src` back at the original
 * source and drop `srcset` / `sizes` / `width` / `height`, so the browser loads
 * the original at its natural size. Returns `true` when it changed the image,
 * `false` when the image was not a Cloudflare variant (nothing to undo).
 *
 * For images whose overlay is defined in the ORIGINAL's pixel space — the old
 * generator's IPL drawings with natural-pixel `data-finden` buttons (dana2
 * rollback rehearsal 2026-10-05: a 1280w variant put button 14 at top 197%).
 * Production serves those drawings unresized, so this restores its behaviour
 * exactly; fraction-coordinate drawings do not need it and stay responsive.
 */
export function restoreOriginalImage(img: HTMLImageElement): boolean {
  const src = img.getAttribute('src') ?? '';
  const firstCandidate = (img.getAttribute('srcset') ?? '').trim().split(/\s+/)[0] ?? '';
  const original = cloudflareSourceOf(src) ?? cloudflareSourceOf(firstCandidate);
  if (original === null) return false;

  img.removeAttribute('srcset');
  img.removeAttribute('sizes');
  img.removeAttribute('width');
  img.removeAttribute('height');
  img.setAttribute('src', original);
  return true;
}
